<!-- 
Dashboard widget showing invoices by status.

This widget has two modes: one shows the count of invoices by status, the other shows the $ value of invoices by status.
-->
<template>
  <v-flex xs12 md6 lg4 xl3>
    <v-card flat class="home">
      <our-widget-toolbar :title="title" @refresh="forceReload" @remove="$emit('removeWidget')" @toggleChartType="$emit('toggle')" />
      <our-busy-indicator :show="!loaded"/>
      <v-card-text class="pb-3">
        <bar-chart 
          v-if="!isChartEmpty(chartData)" ref="chart" :chart-data="chartData" :chart-options="chartOptions" 
          style="height: 265px"
        />
        <our-chart-is-empty-placeholder v-if="isChartEmpty(chartData)" style="height: 265px" />
      </v-card-text>
    </v-card>
  </v-flex>
</template>

<script>
import _ from "underscore";
import BarChart from "@/components/common/chart/BarChart.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import * as formatting from "@/helpers/formatting.helper";
import * as dashboardAction from "@/store/dashboard/action-types";
import * as action from "@/store/invoices/action-types";
import * as mutation from "@/store/invoices/mutation-types";
import chartMixins from "@/mixins/chart.mixins";

export default {
  components: {
    BarChart
  },
  mixins: [lookupMixins, chartMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    toggled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: this.chartClick,
        onHover: this.chartHover,
        tooltips: {
          callbacks: {
            label: this.showLabel
          }
        },
        scales: {
          yAxes: [{ ticks: { min: 0, callback: this.formatYAxisTicks }, afterBuildTicks: this.setYAxesTicks }]
        }
      }
    };
  },
  computed: {
    state() {
      return this.$store.state.dashboard.invoiceStatus;
    },
    loading() {
      return this.state.loading;
    },
    loaded() {
      return this.state.loaded;
    },
    title() {
      return this.toggled ? "Invoice $ by status" : "Invoices by status";
    },
    chartData() {
      return {
        labels: _.map(this.state.data, d => {
          return this.lookups.invoiceStatusDescriptions[d.status];
        }),
        datasets: [
          {
            data: _.map(this.state.data, d => {
              return this.toggled ? d.totalLevy : d.count;
            })
          }
        ]
      };
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    },
    toggled() {
      this.drawChart();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    forceReload() {
      this.$store.dispatch(dashboardAction.INVOICE_STATUS_GET, this.levyCollectionId);
    },
    refresh() {
      this.$store.dispatch(dashboardAction.INVOICE_STATUS_REFRESH, this.levyCollectionId);
      if (this.loaded) {
        this.drawChart();
      }
    },
    drawChart() {
      this.$nextTick(() => {
        if (this.$refs.chart) this.$refs.chart.display();
      });
    },
    removeWidget() {
      this.$emit("removeWidget");
    },
    chartClick: function(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = this.isDoughnut ? chartObject.getElementsAtEvent(e) : chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const idx = activePoints[0]["_index"];
        const number = this.chartData.datasets[0].data[idx];
        if (number > 0) {
          const label = this.chartData.labels[idx];
          const status = _.find(this.lookups.invoiceStatuses, l => l.text == label).value;
          this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
          this.$store.commit(mutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId, providerId: null });
          this.$store.commit(mutation.LIST_SET_CRITERIA, { status: [status] });
          this.$store.dispatch(action.LIST);
          this.$emit("showInvoices");
        }
      }
    },
    chartHover: function(e) {
      const chartObject = this.$refs.chart.$data._chart;
      const activePoints = chartObject.getElementsAtXAxis(e);
      if (activePoints[0]) {
        const idx = activePoints[0]["_index"];
        const number = this.chartData.datasets[0].data[idx];
        if (number > 0) {
          chartObject.canvas.style.cursor = "pointer";
          return;
        }
      }
      chartObject.canvas.style.cursor = "default";
    },
    showLabel: function(tooltipItem, data) {
      const index = tooltipItem.index;
      const number = data.datasets[0].data[index];
      var label = this.toggled ? formatting.currency(number) : formatting.formatNumber(number);
      if (number > 0) {
        label += " (click to show)";
      }
      return label;
    },
    setYAxesTicks(axis) {
      return _(axis.ticks).filter(t => t == Math.trunc(t));
    },
    formatYAxisTicks(label) {
      return this.toggled ? formatting.currency0(label) : label;
    }
  }
};
</script>
